import { useEffect, useState } from 'react'
import { Outlet, NavLink, useLocation } from 'react-router-dom'
import { Suspense } from 'react'
import { LoadingScreen } from './ui'
import { Bell, BookMarked, BookOpen, ChevronRight, CircleHelp, House, Layers, Menu, MessageCircle, NotebookPen, Settings2, ShieldCheck, Video, WifiOff, X } from 'lucide-react'
import { DownloadPanel } from './DownloadPanel'
import { VideoMediaBoundary } from './VideoMediaBoundary'
import { useResizableSidebar } from '../hooks/useResizableSidebar'
import clsx from 'clsx'
import { EmptyState } from './ui'
import { useAppStore } from '../store/app-store'
import { repository } from '../services/repository'
import type { AppNotification } from '../types'
import { formatDistanceToNow } from 'date-fns'

const primaryNav = [
  { to: '/', label: 'Dashboard', icon: House, isAction: false, hasChevron: false },
  { to: '/qbanks', label: 'QBank', icon: CircleHelp, isAction: false, hasChevron: true },
  { to: '/library', label: 'Medical Library', icon: BookOpen, isAction: false, hasChevron: false },
  { to: '/videos', label: 'Video Lectures', icon: Video, isAction: false, hasChevron: false },
  { to: '/flashcards', label: 'Flashcards', icon: Layers, isAction: false, hasChevron: true },
  { to: '/books', label: 'Books', icon: BookMarked, isAction: false, hasChevron: false },
  { to: '/notes', label: 'My Notebook', icon: NotebookPen, isAction: false, hasChevron: false },
  { to: '/community', label: 'Help', icon: MessageCircle, isAction: false, hasChevron: false },
  { to: '#notifications', label: 'Notifications', icon: Bell, isAction: true, hasChevron: false },
]

function getPageTitle(pathname: string): string {
  if (pathname === '/') return 'Dashboard'
  if (pathname.startsWith('/cloud-store')) return 'Download Center'
  if (pathname.startsWith('/qbanks') || pathname.startsWith('/quiz')) return 'QBanks'
  if (pathname.startsWith('/library')) return 'Medical Library'
  if (pathname.startsWith('/videos')) return 'Video Lectures'
  if (pathname.startsWith('/flashcards')) return 'Flashcards'
  if (pathname.startsWith('/books')) return 'Books'
  if (pathname.startsWith('/notes')) return 'My Notebook'
  if (pathname.startsWith('/community')) return 'Community'
  if (pathname.startsWith('/profile')) return 'My Profile'
  if (pathname.startsWith('/admin')) return 'Admin'
  if (pathname.startsWith('/search')) return 'Search'
  if (pathname.startsWith('/ai-tutor')) return 'AI Tutor'
  if (pathname.startsWith('/bookmarks')) return 'Bookmarks'
  if (pathname.startsWith('/analytics')) return 'Analytics'
  if (pathname.startsWith('/subscription')) return 'Subscription'
  if (pathname.startsWith('/quick-fire')) return 'QuickFire'
  if (pathname.startsWith('/clinical-cases')) return 'Clinical Cases'
  if (pathname.startsWith('/cloud-store')) return 'Content Library'
  if (pathname.startsWith('/study')) return 'Study'
  return 'Dashboard'
}

export function AppShell() {
  const { user, sidebarOpen, setSidebarOpen, notificationOpen, setNotificationOpen, online } = useAppStore()
  const { collapsed, effectiveWidth, toggle: toggleCollapsed, dividerProps } = useResizableSidebar()
  const [notifications, setNotifications] = useState<AppNotification[]>([])
  const location = useLocation()

  useEffect(() => { setSidebarOpen(false) }, [location.pathname, setSidebarOpen])
  useEffect(() => { let active = true; if (user) void repository.notifications(user.id).then(items => { if (active) setNotifications(items) }).catch(() => { if (active) setNotifications([]) }); return () => { active = false } }, [user, notificationOpen])

  const labelsVisible = !collapsed || sidebarOpen

  // Remove achievement notifications
  const filteredNotifications = notifications.filter(n => n.kind !== 'achievement')
  const unreadCount = filteredNotifications.filter(n => !n.read).length

  // Test sessions and their score reports own the full viewport, matching dedicated exam software.
  if (location.pathname === '/quiz/session' || location.pathname === '/quiz/results') return <div className="test-session-host"><VideoMediaBoundary key={location.pathname}><Outlet /></VideoMediaBoundary></div>

  return (
    <div className={clsx('app-shell imed-app', collapsed && 'sidebar-collapsed')} data-page={location.pathname.split('/')[1] || 'home'} style={{ gridTemplateColumns: `${effectiveWidth}px minmax(0,1fr)` }}>
      <aside className={clsx('sidebar', sidebarOpen && 'mobile-open')} style={{ width: effectiveWidth }}>

        {/* UWorld Style Sidebar Brand Header — Logo on TOP, and beneath only iMed */}
        <div className="sidebar-brand-uworld">
          <div className="sidebar-brand-logo-circle">
            <img src="./imed-brand-icon.png" alt="iMed" className="sidebar-brand-logo-img" />
          </div>
          {labelsVisible && <span className="sidebar-brand-name-uworld">iMed</span>}
          <button className="mobile-close" onClick={() => setSidebarOpen(false)} aria-label="Close navigation"><X size={18} /></button>
        </div>

        <nav aria-label="Primary navigation">
          {primaryNav.map(({ to, label, icon: Icon, isAction, hasChevron }) => {
            if (isAction) {
              return (
                <button
                  key={to}
                  className={clsx('nav-item', notificationOpen && 'active')}
                  title={collapsed ? label : undefined}
                  onClick={() => setNotificationOpen(true)}
                  style={{ position: 'relative' }}
                >
                  <Icon size={18} strokeWidth={1.8} />
                  {labelsVisible && <span>{label}</span>}
                  {unreadCount > 0 && (
                    <span
                      style={{
                        position: collapsed ? 'absolute' : 'relative',
                        top: collapsed ? 8 : 'auto',
                        right: collapsed ? 10 : 'auto',
                        marginLeft: collapsed ? 0 : 'auto',
                        background: '#ef4444',
                        color: '#fff',
                        fontSize: '11px',
                        fontWeight: 800,
                        padding: '1px 6px',
                        borderRadius: '99px',
                        minWidth: '16px',
                        textAlign: 'center',
                        lineHeight: 1.4,
                      }}
                    >
                      {unreadCount}
                    </span>
                  )}
                </button>
              )
            }
            return (
              <NavLink
                key={to}
                to={to}
                end={to === '/'}
                title={collapsed ? label : undefined}
                className={({ isActive }) =>
                  clsx(
                    'nav-item',
                    (isActive || (to === '/qbanks' && location.pathname.startsWith('/quiz'))) && 'active'
                  )
                }
              >
                <Icon size={18} strokeWidth={1.8} />
                {labelsVisible && <span>{label}</span>}
                {labelsVisible && hasChevron && (
                  <ChevronRight size={13} strokeWidth={2} className="nav-item-chevron" />
                )}
              </NavLink>
            )
          })}
        </nav>

        <div className="sidebar-footer">
          <NavLink
            to="/profile"
            title={collapsed ? (user?.name || 'Profile') : undefined}
            className={({ isActive }) => clsx('nav-item profile-sidebar-btn', isActive && 'active')}
          >
            <span className="sidebar-user-avatar">
              {(user?.name || 'S').slice(0, 1).toUpperCase()}
            </span>
            {labelsVisible && (
              <div className="sidebar-user-info">
                <span className="sidebar-user-name">{user?.name || 'Saleem Ullah'}</span>
                <span className="sidebar-user-role">{user?.role === 'admin' ? 'Administrator' : 'Student Account'}</span>
              </div>
            )}
          </NavLink>

        </div>

      </aside>

      <div {...dividerProps} className="sidebar-divider" style={{ left: Math.max(0, effectiveWidth - 5) }}><span /></div>

      {sidebarOpen && <button className="sidebar-scrim" onClick={() => setSidebarOpen(false)} aria-label="Close navigation" />}

      <div className="app-main">
        {/* UWorld Topbar — Left: Blue 3-bar button + Page Title; Right: Home link + Settings */}
        <header className="topbar">
          <div className="topbar-left">
            <button
              className="uworld-hamburger-btn"
              onClick={() => {
                if (window.innerWidth <= 900) {
                  setSidebarOpen(!sidebarOpen)
                } else {
                  toggleCollapsed()
                }
              }}
              aria-label="Toggle navigation"
              title={collapsed ? 'Open sidebar' : 'Collapse sidebar'}
            >
              <Menu size={20} />
            </button>
            {getPageTitle(location.pathname) === 'Dashboard' ? (
              <NavLink to="/" className="topbar-brand-logo" title="iMed">
                <img src="./imed-brand-icon.png" alt="iMed" className="topbar-brand-logo-img" />
                <span className="topbar-brand-logo-text">iMed</span>
              </NavLink>
            ) : (
              <h1 className="topbar-page-title">{getPageTitle(location.pathname)}</h1>
            )}
          </div>

          <div className="topbar-actions">
            {!online && <span className="offline-pill"><WifiOff size={14} /> Offline</span>}
            {location.pathname !== '/' && location.pathname !== '/qbanks' && (
              <>
                <NavLink to="/" className="topbar-home-btn" title="Dashboard Home">
                  <House size={16} />
                  <span>Home</span>
                </NavLink>
                {!location.pathname.startsWith('/library/source') && (
                  <NavLink to="/profile" className={({ isActive }) => clsx('topbar-settings-btn', isActive && 'active')} title="Profile & Settings">
                    <Settings2 size={19} />
                  </NavLink>
                )}
              </>
            )}
          </div>
        </header>
        <main className={clsx('content', (location.pathname === '/' || location.pathname === '/qbanks') && 'content-full-bleed')}>
          <Suspense fallback={<LoadingScreen inline />}><VideoMediaBoundary key={location.pathname}><Outlet /></VideoMediaBoundary></Suspense>
        </main>
      </div>

      <DownloadPanel />
      <div className={clsx('drawer-scrim', notificationOpen && 'visible')} onClick={() => setNotificationOpen(false)} />
      <aside className={clsx('notification-drawer', notificationOpen && 'open')}>
        <header><div><p className="eyebrow">Stay on track</p><h2>Notifications</h2></div><button className="icon-button" aria-label="Close notifications" onClick={() => setNotificationOpen(false)}><X /></button></header>
        <div className="drawer-list">{filteredNotifications.length ? filteredNotifications.map((item) => <button key={item.id} className={clsx('notification-item', !item.read && 'unread')} onClick={async () => { await repository.markNotificationRead(item.id); setNotifications((all) => all.map((n) => n.id === item.id ? { ...n, read: true } : n)) }}><span className="notification-dot" /><div><b>{item.title}</b><p>{item.message}</p><small>{formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}</small></div></button>) : <EmptyState icon={Bell} title="You're all caught up" description="Study reminders will appear here." />}</div>
      </aside>
    </div>
  )
}
